import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, MessageSquare, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface AIAgent {
  id: string;
  name: string;
  role: string;
  systemPrompt: string;
  model: string;
  apiKey: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

const models = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (Standard)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (Leistungsstark)' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (Schnell)' },
  { value: 'openai/gpt-5', label: 'GPT-5 (Premium)' }
];

export const AIAgentsManager = () => {
  const [agents, setAgents] = useState<AIAgent[]>(() => {
    const saved = localStorage.getItem('ai-agents');
    return saved ? JSON.parse(saved) : [];
  });

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [newAgent, setNewAgent] = useState({
    name: '',
    role: '',
    systemPrompt: '',
    model: 'google/gemini-2.5-flash'
  });

  const generateApiKey = () => {
    return `eap_agent_${crypto.randomUUID().replace(/-/g, '')}`;
  };

  const addAgent = () => {
    if (!newAgent.name || !newAgent.role || !newAgent.systemPrompt) {
      toast.error('Name, Rolle und System-Prompt sind erforderlich');
      return;
    }

    const agent: AIAgent = {
      id: crypto.randomUUID(),
      ...newAgent,
      apiKey: generateApiKey(),
      status: 'active',
      createdAt: new Date().toISOString()
    };

    const updated = [...agents, agent];
    setAgents(updated);
    localStorage.setItem('ai-agents', JSON.stringify(updated));
    
    setNewAgent({
      name: '',
      role: '',
      systemPrompt: '',
      model: 'google/gemini-2.5-flash'
    });

    toast.success('KI-Agent erstellt');
  };

  const deleteAgent = (id: string) => {
    const updated = agents.filter(a => a.id !== id);
    setAgents(updated);
    localStorage.setItem('ai-agents', JSON.stringify(updated));
    toast.success('KI-Agent gelöscht');
  };

  const toggleStatus = (id: string) => {
    const updated = agents.map(a => 
      a.id === id ? { ...a, status: a.status === 'active' ? 'inactive' : 'active' } as AIAgent : a
    );
    setAgents(updated);
    localStorage.setItem('ai-agents', JSON.stringify(updated));
    toast.success('Status aktualisiert');
  };

  const copyToClipboard = (text: string, agentId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(agentId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('In Zwischenablage kopiert');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            KI-Agenten Verwaltung
          </CardTitle>
          <CardDescription>
            Erstellen und verwalten Sie KI-Agenten mit dedizierter API für externe Apps
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border-b pb-6">
            <h3 className="text-lg font-semibold mb-4">Neuen KI-Agenten erstellen</h3>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name des Agenten*</Label>
                  <Input
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    placeholder="z.B. EAP® Support Assistent"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rolle*</Label>
                  <Input
                    value={newAgent.role}
                    onChange={(e) => setNewAgent({ ...newAgent, role: e.target.value })}
                    placeholder="z.B. Kundenberater, Technischer Support"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>KI-Modell</Label>
                <Select
                  value={newAgent.model}
                  onValueChange={(value) => setNewAgent({ ...newAgent, model: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map(model => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>System-Prompt*</Label>
                <Textarea
                  value={newAgent.systemPrompt}
                  onChange={(e) => setNewAgent({ ...newAgent, systemPrompt: e.target.value })}
                  placeholder="Definieren Sie die Persönlichkeit und Aufgaben des Agenten..."
                  rows={6}
                />
                <p className="text-xs text-muted-foreground">
                  Der System-Prompt definiert, wie sich der Agent verhält und welche Aufgaben er erfüllt
                </p>
              </div>

              <Button onClick={addAgent} className="w-full">
                <Bot className="mr-2 h-4 w-4" />
                Agent erstellen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {agents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Aktive Agenten ({agents.length})</CardTitle>
            <CardDescription>
              Verwalten Sie Ihre KI-Agenten und API-Zugänge
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {agents.map((agent) => (
                <div key={agent.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-lg">{agent.name}</h4>
                        <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                          {agent.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{agent.role}</p>
                      <p className="text-xs text-muted-foreground">
                        Erstellt: {new Date(agent.createdAt).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleStatus(agent.id)}
                      >
                        {agent.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAgent(agent.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  <div className="bg-muted/50 p-3 rounded text-sm">
                    <strong>System-Prompt:</strong>
                    <p className="mt-1 whitespace-pre-line">{agent.systemPrompt}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded">
                      <div className="space-y-1 flex-1">
                        <p className="text-xs font-medium text-muted-foreground">API Key</p>
                        <code className="text-xs font-mono">{agent.apiKey}</code>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(agent.apiKey, agent.id)}
                      >
                        {copiedId === agent.id ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded">
                      <div className="space-y-1 flex-1">
                        <p className="text-xs font-medium text-muted-foreground">Agent ID</p>
                        <code className="text-xs font-mono">{agent.id}</code>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(agent.id, `${agent.id}-id`)}
                      >
                        {copiedId === `${agent.id}-id` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-medium mb-1">API Endpoint:</p>
                      <code className="text-xs font-mono break-all">
                        POST {import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-chat
                      </code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Integration mit externer App</CardTitle>
          <CardDescription>
            So integrieren Sie den KI-Agenten in Ihre Anwendung
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">1. API Request</h4>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
{`POST ${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-chat
Content-Type: application/json

{
  "agentId": "AGENT_ID",
  "apiKey": "AGENT_API_KEY",
  "message": "Ihre Nachricht hier",
  "conversationId": "optional-conversation-id"
}`}
              </pre>
            </div>

            <div>
              <h4 className="font-semibold mb-2">2. Response</h4>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
{`{
  "response": "Antwort des KI-Agenten",
  "conversationId": "conversation-uuid",
  "agentName": "Name des Agenten"
}`}
              </pre>
            </div>

            <div>
              <h4 className="font-semibold mb-2">3. JavaScript Beispiel</h4>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
{`const response = await fetch(
  '${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-chat',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      agentId: 'YOUR_AGENT_ID',
      apiKey: 'YOUR_API_KEY',
      message: 'Hallo, ich brauche Hilfe'
    })
  }
);
const data = await response.json();
console.log(data.response);`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
